import { styled } from "@mui/material/styles";
import { Box, ButtonBase, Chip, LinearProgress } from "@mui/material";
import { blue, grey } from "@mui/material/colors";

/**
 * Windows 11 Design System Styled Components
 *
 * Specifications:
 * - Border Radius: 8px (small), 12px (medium), 16px (large)
 * - Shadows: Subtle elevation with rgba(0,0,0,0.08-0.15)
 * - Colors: Softer palette, #f8fafc surfaces, #e2e8f0 borders
 * - Transitions: 200-300ms cubic-bezier(0.4, 0, 0.2, 1)
 */

// ============================================
// 1. COMMAND BAR CONTAINER
// ============================================

export const CommandBarContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(1.5),
  padding: 0,
  minHeight: 72,
  backgroundColor: "#f8fafc",
  borderRadius: 12,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",

  [theme.breakpoints.down("lg")]: {
    gap: theme.spacing(0.75),
    minHeight: 56,
  },
}));

// ============================================
// 2. WINDOWS 11 CARD (Product Cards)
// ============================================

export const Windows11Card = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  borderRadius: 8,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",

  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
  },

  // Reduced motion support
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:hover": {
      transform: "none",
    },
  },
}));

// ============================================
// 3. COMPACT CUSTOMER CHIP
// ============================================

export const CompactCustomerChip = styled(ButtonBase)(({ theme, filled }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: "12px 16px",
  minWidth: 180,
  borderRadius: 8,
  border: `1px solid ${filled ? blue[200] : "#e2e8f0"}`,
  backgroundColor: filled ? blue[50] : "#ffffff",
  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    backgroundColor: filled ? blue[100] : grey[50],
    borderColor: filled ? blue[300] : grey[300],
  },

  [theme.breakpoints.down("lg")]: {
    minWidth: "auto",
    padding: "10px 10px",
  },
}));

// ============================================
// 4. CART SUMMARY CHIP
// ============================================

export const CartSummaryChip = styled(ButtonBase)(
  ({ theme, filled, itemcount }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: "12px 16px",
    minWidth: 160,
    borderRadius: 8,
    border: filled ? "none" : `1px solid #e2e8f0`,
    backgroundColor: filled ? theme.palette.primary.main : "#ffffff",
    color: filled ? "#ffffff" : theme.palette.text.primary,
    fontWeight: 600,
    boxShadow: filled
      ? "0 2px 8px rgba(37, 99, 235, 0.3)"
      : "0 2px 8px rgba(0,0,0,0.08)",
    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",

    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: filled
        ? "0 4px 12px rgba(37, 99, 235, 0.4)"
        : "0 4px 12px rgba(0,0,0,0.12)",
      backgroundColor: filled ? blue[700] : grey[50],
    },

    [theme.breakpoints.down("lg")]: {
      minWidth: "auto",
      padding: "10px 10px",
    },

    // Reduced motion support
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover": {
        transform: "none",
      },
    },
  })
);

// ============================================
// 5. SEGMENTED CONTROL (Order Type Selector)
// ============================================

export const SegmentedControlContainer = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  backgroundColor: grey[100],
  borderRadius: 8,
  padding: 4,
  gap: 4,

  [theme.breakpoints.down("lg")]: {
    padding: 2,
  },
}));

export const SegmentedControlButton = styled(ButtonBase)(
  ({ theme, selected }) => ({
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: "10px 20px",
    minWidth: 110,
    borderRadius: 6,
    backgroundColor: selected ? "#ffffff" : "transparent",
    color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
    fontWeight: selected ? 600 : 500,
    fontSize: "0.875rem",
    boxShadow: selected ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",

    "&:hover": {
      backgroundColor: selected ? "#ffffff" : grey[50],
    },

    "& .MuiSvgIcon-root": {
      fontSize: "1.25rem",
    },

    [theme.breakpoints.down("lg")]: {
      minWidth: "auto",
      padding: "8px 10px",
    },
  })
);

// ============================================
// 6. COMMAND BAR DIVIDER
// ============================================

export const CommandBarDivider = styled(Box)(({ theme }) => ({
  width: 1,
  height: 40,
  backgroundColor: "#e2e8f0",
  flexShrink: 0,

  [theme.breakpoints.down("lg")]: {
    display: "none",
  },
}));

// ============================================
// 7. WINDOWS 11 CATEGORY BUTTON
// ============================================

export const Windows11CategoryButton = styled(ButtonBase)(
  ({ theme, colorhex, isselected }) => ({
    borderRadius: 8, // Changed from 50px pill to 8px rounded rectangle
    padding: "8px 20px",
    minHeight: 36,
    border: `1px solid ${isselected ? colorhex : "#e2e8f0"}`,
    backgroundColor: isselected ? `${colorhex}15` : grey[50], // 15 = ~9% opacity
    color: isselected ? colorhex : theme.palette.text.primary,
    fontWeight: isselected ? 600 : 500,
    fontSize: "0.875rem",
    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
    whiteSpace: "nowrap",

    // Shadow ring effect when selected (instead of border-bottom)
    boxShadow: isselected
      ? `0 0 0 3px ${colorhex}20, 0 2px 4px rgba(0,0,0,0.08)`
      : "0 1px 2px rgba(0,0,0,0.05)",

    "&:hover": {
      backgroundColor: isselected ? `${colorhex}25` : grey[100],
      boxShadow: isselected
        ? `0 0 0 3px ${colorhex}30, 0 2px 6px rgba(0,0,0,0.12)`
        : "0 2px 4px rgba(0,0,0,0.1)",
    },

    // Reduced motion support
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },
  })
);

// ============================================
// 8. PAYMENT BUTTON (Compact)
// ============================================

export const CompactPaymentButtonStyled = styled(ButtonBase)(
  ({ theme, variant = "payment" }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: "13px 24px",
    width: "100%",
    borderRadius: 8,
    backgroundColor: "transparent",
    border: `2px solid ${variant === "payment" ? theme.palette.primary.main : "#16a34a"}`,
    color: variant === "payment" ? theme.palette.primary.main : "#16a34a",
    fontWeight: 600,
    fontSize: "0.875rem",
    boxShadow: "none",
    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",

    "&:hover": {
      backgroundColor:
        variant === "payment"
          ? "rgba(37, 99, 235, 0.06)"
          : "rgba(22, 163, 74, 0.06)",
      transform: "translateY(-1px)",
    },

    "&:disabled": {
      borderColor: grey[300],
      color: grey[400],
      cursor: "not-allowed",
    },

    // Reduced motion support
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover": {
        transform: "none",
      },
    },
  })
);

// ============================================
// 10. PAYMENT SUMMARY PANEL (Sticky Bottom)
// ============================================

export const PaymentSummaryPanel = styled(Box)(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: "#ffffff",
  borderTop: "1px solid #e2e8f0",
  borderRadius: "16px 16px 0 0",
  boxShadow: "0 -4px 12px rgba(0,0,0,0.08)",
  padding: "20px 24px",
  zIndex: 100,
  transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",

  // Mobile: reduced padding
  [theme.breakpoints.down("md")]: {
    padding: "16px",
  },
}));

// ============================================
// 11. PAYMENT METHOD CHIP (Deletable)
// ============================================

export const PaymentMethodChip = styled(Chip)(({ theme }) => ({
  height: 48,
  borderRadius: 8,
  padding: "0 16px",
  fontSize: "0.875rem",
  fontWeight: 600,
  backgroundColor: theme.palette.primary.light,
  border: `1px solid ${theme.palette.primary.main}`,
  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",

  "& .MuiChip-deleteIcon": {
    color: theme.palette.primary.dark,
    fontSize: 20,
    "&:hover": {
      color: theme.palette.error.main,
    },
  },

  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
  },

  // Mobile: larger touch target
  [theme.breakpoints.down("sm")]: {
    height: 52,
    fontSize: "0.9375rem",
    padding: "0 18px",
  },

  // Reduced motion support
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:hover": {
      transform: "none",
    },
  },
}));

// ============================================
// 12. DISCOUNT CHIP (Deletable)
// ============================================

export const DiscountChip = styled(Chip)(({ theme }) => ({
  height: 48,
  borderRadius: 8,
  padding: "0 16px",
  fontSize: "0.875rem",
  fontWeight: 600,
  backgroundColor: theme.palette.secondary.light,
  border: `1px solid ${theme.palette.secondary.main}`,
  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",

  "& .MuiChip-deleteIcon": {
    color: theme.palette.secondary.dark,
    fontSize: 20,
    "&:hover": {
      color: theme.palette.error.main,
    },
  },

  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
  },

  // Mobile: larger touch target
  [theme.breakpoints.down("sm")]: {
    height: 52,
    fontSize: "0.9375rem",
    padding: "0 18px",
  },

  // Reduced motion support
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:hover": {
      transform: "none",
    },
  },
}));

// ============================================
// 13. ADD ACTION CHIP (Dashed Border)
// ============================================

export const AddActionChip = styled(ButtonBase)(({ theme }) => ({
  height: 48,
  minWidth: 140,
  borderRadius: 8,
  padding: "0 16px",
  fontSize: "0.875rem",
  fontWeight: 500,
  backgroundColor: "#ffffff",
  border: "2px dashed #e2e8f0",
  color: theme.palette.text.secondary,
  display: "flex",
  alignItems: "center",
  gap: 8,
  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary[50],
    color: theme.palette.primary.main,
    transform: "translateY(-1px)",
  },

  "&:disabled": {
    borderColor: grey[200],
    color: grey[400],
    cursor: "not-allowed",
    "&:hover": {
      backgroundColor: "#ffffff",
      transform: "none",
    },
  },

  // Mobile: larger touch target
  [theme.breakpoints.down("sm")]: {
    height: 52,
    fontSize: "0.9375rem",
    padding: "0 18px",
    minWidth: 150,
  },

  // Reduced motion support
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:hover": {
      transform: "none",
    },
  },
}));

// ============================================
// 14. LINEAR PAYMENT PROGRESS
// ============================================

export const LinearPaymentProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[200],

  "& .MuiLinearProgress-bar": {
    borderRadius: 4,
    backgroundColor: theme.palette.primary.main,
    transition: "all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)", // Spring easing
  },

  // Reduced motion support
  "@media (prefers-reduced-motion: reduce)": {
    "& .MuiLinearProgress-bar": {
      transition: "none",
    },
  },
}));

// ============================================
// 15. SERVICE FEE TOGGLE CHIP
// ============================================

export const ServiceFeeToggleChip = styled(Chip)(({ applied }) => ({
  height: 40,
  borderRadius: 8,
  padding: "0 12px",
  fontSize: "0.875rem",
  fontWeight: 600,
  backgroundColor: applied ? "#dcfce7" : "#fff7ed", // green[50] : orange[50]
  border: applied ? "1px solid #22c55e" : "1px solid #f97316", // green : orange
  color: applied ? "#15803d" : "#c2410c", // green[700] : orange[700]
  cursor: "pointer",
  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },

  // Reduced motion support
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:hover": {
      transform: "none",
    },
  },
}));

// ============================================
// 16. SECTION CONTAINER
// ============================================

export const SectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8fafc", // grey[50]
  borderRadius: 12,
  padding: "20px 24px",
  marginBottom: 16,
  border: "1px solid #e2e8f0",
  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",

  // Mobile: reduced padding
  [theme.breakpoints.down("md")]: {
    padding: "16px",
  },
}));
